import React, { useState } from 'react';
import { errorMessage, listUsers } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import type { OrgUser } from '../../types';
import { Badge, Button, Card, EmptyState, ErrorState, Skeleton, Table, Td, Th } from '../../ui';
import { useAuth } from '../auth/AuthProvider';
import { AddUserDialog } from './AddUserDialog';

const ROLE_LABEL: Record<string, string> = { user: 'User', admin: 'Admin', superadmin: 'Superadmin' };

/**
 * The people in the admin's organisation (GET /users) and a way to add a colleague (POST /users). A superadmin sees users
 * of every organisation, so the organisation column is shown for them. Reached only by admins: the server refuses everyone
 * else with 403, and the sidebar link is hidden for them.
 */
export function UsersPage() {
  const { role, user: me } = useAuth();
  const list = useAsyncData<OrgUser[]>((signal) => listUsers({ signal }), []);
  const [adding, setAdding] = useState(false);
  const users = list.data || [];
  const showOrganisation = role === 'superadmin';

  let body: React.ReactNode;
  if (list.error) {
    body = <ErrorState title="Could not load users" message={errorMessage(list.error)} onRetry={list.reload} />;
  } else if (!list.data) {
    body = <div className="space-y-2 p-5" aria-busy="true">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>;
  } else if (users.length === 0) {
    body = <EmptyState title="No users yet" description="Add a colleague to give them access." action={<Button size="sm" onClick={() => setAdding(true)}>Add user</Button>} />;
  } else {
    body = (
      <Table caption="Users">
        <thead><tr><Th>Username</Th><Th>Name</Th><Th>Email</Th><Th>Role</Th>{showOrganisation && <Th>Organisation</Th>}</tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id || u.username} className="hover:bg-canvas">
              <Td className="font-semibold">{u.username}{me && me.username === u.username && <span className="ml-2 text-xs font-normal text-muted">(you)</span>}</Td>
              <Td className="text-muted">{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</Td>
              <Td className="text-muted">{u.email || '—'}</Td>
              <Td><Badge tone={u.role === 'user' ? 'neutral' : 'brand'}>{ROLE_LABEL[u.role || ''] || u.role || '—'}</Badge></Td>
              {showOrganisation && <Td className="font-mono text-xs text-muted">{u.clientId || '—'}</Td>}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Users</h1>
          <p className="mt-1 text-sm text-muted">People in your organisation. A user sees only the campaigns they create; an admin sees them all.</p>
        </div>
        <Button onClick={() => setAdding(true)}>＋ Add user</Button>
      </div>
      <Card className="overflow-hidden">{body}</Card>
      <AddUserDialog open={adding} onClose={() => setAdding(false)} onAdded={list.reload} />
    </div>
  );
}
